import { useQueue } from '../hooks/useQueue.js';

import type { ReactElement, ReactNode } from 'react';

const HTTP_CAT_402_URL = 'https://http.cat/402';
const HTTP_CAT_402_ALT = 'HTTP 402 — Payment Required';

/**
 * Both callers below are the same fact reaching the console by different routes — Spotify gates
 * the queue read and every playback write behind Premium alike — so they say it with one voice
 * and one picture, and differ only in what the DJ had just tried to do.
 */
const PremiumRequiredCard = ({
  heading,
  children,
}: {
  readonly heading: string;
  readonly children: ReactNode;
}): ReactElement => (
  <div role="alert" className="bg-danger-subtle rounded-card p-4">
    <p className="text-danger text-lg font-semibold">{heading}</p>
    {children}
    <p className="text-secondary mt-2 text-sm">
      Nothing here is retrying, because retrying cannot help. To DJ this channel properly, sign out
      and sign back in with a Premium account.
    </p>
    <img
      src={HTTP_CAT_402_URL}
      alt={HTTP_CAT_402_ALT}
      className="border-subtle rounded-control mt-3 max-h-48 w-auto border"
    />
  </div>
);

// Spotify serves GET /me/player/queue to Premium accounts only; `unavailable`
// reaching here already means the token is good, so Premium is the only
// explanation left (see isPremiumGated in auth/playback-poller.ts).
export const PremiumRequiredBanner = (): ReactElement | null => {
  const queue = useQueue();

  // Signed out, before the first frame, and a merely-empty queue are all
  // silence — only a refusal has anything to report.
  if (queue?.status !== 'unavailable') {
    return null;
  }

  return (
    <PremiumRequiredCard heading="MixWire does not support free Spotify accounts">
      <p className="text-secondary mt-2 text-sm">
        Spotify will not tell us what is up next on a free account — it serves the queue to Premium
        subscribers only, and refuses everyone else outright. Your track above is live and is being
        recorded to the party&apos;s history, but the queue will stay empty for as long as this
        account is signed in, and the crowd&apos;s suggestions have nothing to be checked against.
      </p>
    </PremiumRequiredCard>
  );
};

/**
 * The same refusal, but arrived at by pressing something. The standing banner above only appears
 * once the queue stream has reported back, so a DJ can press Add to queue before it has said
 * anything — and having pressed, they are owed the answer then and there rather than whenever the
 * stream catches up.
 */
export const PremiumRequiredWriteNotice = (): ReactElement => (
  <PremiumRequiredCard heading="That needs a Spotify Premium account">
    <p className="text-secondary mt-2 text-sm">
      Spotify lets only Premium subscribers change what an account is playing, and refuses everyone
      else outright — so MixWire could not put that track on your queue, and cannot skip for you
      either. Nothing has changed on your deck.
    </p>
    <p className="text-secondary mt-2 text-sm">
      You can still DJ by queueing the track in Spotify yourself; the console will pick it up on the
      next poll, and the crowd&apos;s suggestion will clear once it does.
    </p>
  </PremiumRequiredCard>
);
